// Accessible "new list" dialog shared by My Lists and every Add-to-list menu
// (UX-031). role="dialog", labelled, traps Tab, closes on Escape, returns focus
// to whatever opened it, and shows the real server error instead of a silent hang.

import { useEffect, useId, useRef, useState } from 'react'

interface CreateListDialogProps {
  title?: string
  submitLabel?: string
  /** Throw to keep the dialog open and show the error. */
  onSubmit: (name: string, description: string) => Promise<void> | void
  onClose: () => void
}

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'

export default function CreateListDialog({
  title = 'Create New List',
  submitLabel = 'Create',
  onSubmit,
  onClose,
}: CreateListDialogProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const panelRef = useRef<HTMLDivElement>(null)
  const nameRef = useRef<HTMLInputElement>(null)
  const titleId = useId()
  const nameId = useId()
  const descId = useId()

  // Remember the opener, focus the name field, and hand focus back on close.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    nameRef.current?.focus()
    return () => {
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus()
    }
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation()
      if (!busy) onClose()
      return
    }
    if (e.key !== 'Tab' || !panelRef.current) return
    const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE))
    if (items.length === 0) return
    const first = items[0]
    const last = items[items.length - 1]
    const active = document.activeElement
    if (e.shiftKey && active === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && active === last) {
      e.preventDefault()
      first.focus()
    } else if (!panelRef.current.contains(active)) {
      e.preventDefault()
      first.focus()
    }
  }

  const submit = async () => {
    if (!name.trim() || busy) return
    setBusy(true)
    setError('')
    try {
      await onSubmit(name.trim(), description.trim())
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string }
      setError(e?.response?.data?.error || e?.message || 'Failed to create list')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onKeyDown={handleKeyDown}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="absolute inset-0 bg-black/40" onClick={() => !busy && onClose()} aria-hidden="true" />
      <div ref={panelRef} className="relative bg-white rounded-2xl border border-iflow-100 shadow-2xl max-w-md w-full mx-4 p-6">
        <h3 id={titleId} className="text-lg font-semibold text-iflow-900 mb-4">{title}</h3>
        <form
          onSubmit={(e) => { e.preventDefault(); void submit() }}
          className="space-y-4"
        >
          <div>
            <label htmlFor={nameId} className="block text-sm font-medium text-slate-600 mb-1">Name</label>
            <input
              id={nameId}
              ref={nameRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 light-input text-sm outline-none"
              placeholder="List name"
            />
          </div>
          <div>
            <label htmlFor={descId} className="block text-sm font-medium text-slate-600 mb-1">Description</label>
            <textarea
              id={descId}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 light-input text-sm resize-none outline-none"
            />
          </div>
          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} disabled={busy} className="px-4 py-2 text-sm font-medium text-slate-600 rounded-lg hover:bg-slate-100">
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || busy}
              className="px-4 py-2 text-sm font-medium text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50"
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
